// src/components/Modal.tsx
import React, { useEffect } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode; // Content to display inside the modal
  title?: string; // Optional title for the modal header
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, title }) => {
  // Effect to handle Escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent background scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    }

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Ensure scrolling is re-enabled when component unmounts while open
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Render nothing if the modal is not open
  if (!isOpen) {
    return null;
  }

  // Stop propagation prevents closing modal when clicking inside content
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    // Overlay backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4 transition-opacity duration-300 ease-in-out"
      onClick={onClose} // Close modal when clicking the backdrop
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined} // Only add aria-labelledby if title exists
    >
      {/* Modal Content Container */}
      <div
        className="bg-white dark:bg-darkbg rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
        onClick={handleContentClick} // Prevent closing when clicking content
      >
        {/* Modal Header (Optional) */}
        {title && (
          <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 id="modal-title" className="text-xl font-semibold text-gray-800 dark:text-gray-100 truncate pr-4">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-darkbg rounded-full p-1"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Modal Body (Scrollable Content) */}
        <div className="p-4 md:p-6 overflow-y-auto flex-grow text-gray-800 dark:text-gray-200">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;